import { useQuery } from "@apollo/client"
import { useParams, useNavigate } from "react-router-dom"
import { gql } from "~/__generated__"
import { adminAdminUsersPath, adminUsersPath } from "~/common/paths"
import { useGoBack } from "~/common/use-go-back"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { useToast } from "~/ui/use-toast"
import { WalletDisplay } from "~/ui/wallet-display"
import { UserAvatar } from "~/users/user-avatar"
import loaderIcon from "~/images/icons/loader-icon"
import { UserPhoneStatus } from "~/__generated__/graphql"

const query = gql(/* GraphQL */ `
  query AdminUserProfile($userId: ID!) {
    node(id: $userId) {
      ... on User {
        id
        firstName
        lastName
        fullName
        createdAt
        emails {
          id
          email
          status
        }
        phones {
          id
          phone
          phoneFormatted
          status
        }
        wallets {
          id
          publicKey
          publicKeyFormatted
        }
        avatarUrl
        avatarThumbUrl
      }
    }
  }
`)

const DELETE_USER_MUTATION = gql(/* GraphQL */ `
  mutation DeleteUser($input: UserDeleteInput!) {
    userDelete(input: $input) {
      user {
        id
      }
    }
  }
`)

export function AdminUserProfileScreen() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const goBack = useGoBack(adminAdminUsersPath({}))
  const { toast } = useToast()

  const [deleteUser, { loading: deleteUserLoading }] = useSafeMutation(DELETE_USER_MUTATION, {
    onCompleted: () => {
      toast({
        title: "User scheduled for deletion",
        description: "The user has been scheduled for deletion.",
      })
      navigate(adminUsersPath({}))
    },
    onError: (error) => {
      console.error("Error deleting user:", error)
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive",
      })
    },
  })

  const handleRemoveUser = async () => {
    if (!user) return

    const userName =
      user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "this user"

    const confirmed = window.confirm(
      `Are you sure you want to remove ${userName}? This will schedule the user for deletion and cannot be undone.`
    )

    if (!confirmed) return

    try {
      await deleteUser({
        variables: {
          input: {
            id: userId!,
          },
        },
      })
    } catch {
      // Error handling is done in the mutation's onError callback
    }
  }

  const { data, loading, error } = useQuery(query, {
    variables: { userId: userId! },
    skip: !userId,
  })

  if (error) {
    return <GraphqlError error={error} />
  }

  if (loading) {
    return (
      <TablePageLayout>
        <div className="flex justify-center py-8">
          <img {...loaderIcon} alt="Loading..." className="animate-spin" />
        </div>
      </TablePageLayout>
    )
  }

  const user = data?.node?.__typename === "User" ? data.node : null

  if (!user) {
    return (
      <TablePageLayout>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <h1 className="mb-4 text-2xl">User not found</h1>
            <p className="text-gray-500">No user found with ID: {userId}</p>
          </div>
        </div>
      </TablePageLayout>
    )
  }

  return (
    <TablePageLayout>
      <div className="flex flex-1 flex-col">
        <button onClick={goBack} className="mb-4 text-left text-blue-600 hover:text-blue-800">
          &larr; <span className="align-text-bottom text-xs font-bold uppercase">Back</span>
        </button>
        <div className="bottom-1 mb-4 flex items-center justify-between border-b border-gray-300 py-4">
          <h1 className="text-2xl">{user.fullName}</h1>
          <Button variant="outline">Edit Details</Button>
        </div>
        <div>
          <UserAvatar
            user={user}
            alt="A picture that represents this user and their account"
            className="mb-8 mt-4 h-16 w-16"
          />
          <table className="flex flex-wrap">
            <tbody>
              <tr>
                <td className="pr-10 text-gray-500">Date Created</td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
              </tr>
              <tr>
                <td className="pr-10 text-gray-500">First Name</td>
                <td>{user.firstName || "-"}</td>
              </tr>
              <tr>
                <td className="pr-10 text-gray-500">Last Name</td>
                <td>{user.lastName || "-"}</td>
              </tr>
              {user.emails && user.emails.length > 0 && (
                <tr>
                  <td className="pr-10 text-gray-500">Email Addresses</td>
                  <td>
                    <div className="space-y-1">
                      {user.emails.map((email) => (
                        <div key={email.id} className="flex items-center gap-2">
                          <span>{email.email}</span>
                          {email.status === "verified" && (
                            <span className="text-sm text-blue-600">verified</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
              {user.phones && user.phones.length > 0 && (
                <tr>
                  <td className="pr-10 text-gray-500">Phone Numbers</td>
                  <td>
                    <div className="space-y-1">
                      {user.phones.map((phone) => (
                        <div key={phone.id} className="flex items-center gap-2">
                          <span>{phone.phoneFormatted || phone.phone}</span>
                          {phone.status === UserPhoneStatus.Verified && (
                            <span className="text-sm text-blue-600">verified</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
              {user.wallets && user.wallets.length > 0 && (
                <tr>
                  <td className="pr-10 text-gray-500">Wallets</td>
                  <td>
                    <div className="space-y-1">
                      {user.wallets.map((wallet) => (
                        <WalletDisplay
                          key={wallet.id}
                          publicKey={wallet.publicKey}
                          publicKeyFormatted={wallet.publicKeyFormatted}
                          showVerified
                          showFullAddress
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-12 border-t border-gray-200 pt-8">
          <button
            onClick={handleRemoveUser}
            disabled={deleteUserLoading}
            className="text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleteUserLoading ? "Removing User..." : "Remove User"}
          </button>
        </div>
      </div>
    </TablePageLayout>
  )
}
